import { styled, TextField } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Button } from "src/components/button";
import SelectMenu from "src/components/selectMenu";
import { SimpleModal } from "src/layouts/modal/simpleModal";
import chevron_top from "src/assets/icons/chevron_top.svg";
import close_icon from "src/assets/icons/close.svg";
import {
  EAuthserverTypeOptions,
  isFieldWithValue,
  isValidEmail,
  isValidURL,
} from "src/utils";
import ICONS from "src/utils/icons";

interface AddEditAuthServerModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  submitTitleButton?: string;
  onSubmit: (data: any) => void;
  defaultValues?: any;
  isLoading?: boolean;
  isEdition?: boolean;
  environments?: { value: string; label: string }[];
}
interface IFormErrors {
  [key: string]: string;
}

interface IBasicCredentials {
  username: string;
  password: string;
  id: string;
}

interface IToken {
  token: string;
  id: string;
}

const defaultFormValues = {
  name: "",
  type: "",
  issuerURL: "",
  redirectPath: "/callback",
  clientId: "",
  clientSecret: "",
  usernameClaim: "",
  emailClaim: "",
  roleClaim: "",
  groupsClaim: "",
  authorizedDomain: "",
  environmentID: "",
};

const CustomTextFieldLeftContent = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderTopLeftRadius: "0px",
    borderBottomLeftRadius: "0px",
  },
});

const CustomTextFieldRightContent = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderTopRightRadius: "0px",
    borderBottomRightRadius: "0px",
  },
});

const AuthserverTypeOptions = [
  { value: EAuthserverTypeOptions.oidc, label: "OIDC" },
  { value: EAuthserverTypeOptions.basic, label: "BASIC" },
  { value: EAuthserverTypeOptions.token, label: "TOKEN" },
];

const defaultBasicCredentials = {
  username: "",
  password: "",
  id: Date.now().toString(),
};

const defaultToken = {
  token: "",
  id: Date.now().toString(),
};

export default function AddEditAuthserver({
  open,
  onClose,
  onSubmit,
  title = "Domain",
  description = "",
  submitTitleButton = "Add",
  defaultValues,
  isLoading = false,
  isEdition = false,
  environments = [],
}: Readonly<AddEditAuthServerModalProps>) {
  const [formData, setFormData] = useState(defaultFormValues);
  const [formErrors, setFormErrors] = useState<IFormErrors>({});
  const [openAccordion, setOpenAccordion] = useState(false);
  const [domains, setDomains] = useState<string[]>([]);
  const [basicCredentials, setBasicCredentials] = useState<IBasicCredentials[]>(
    [defaultBasicCredentials]
  );
  const [tokens, setTokens] = useState<IToken[]>([defaultToken]);
  const [hasValidationError, setHasValidationError] = useState(false);
  const usernameRegex = /^[a-zA-Z][a-zA-Z0-9_.]{2,19}$/;
  const tokenRegex = /^[A-Za-z0-9-_]+$/;

  useEffect(() => {
    setFormData({
      name: defaultValues?.metadata?.name || "",
      type: defaultValues?.spec?.type || "",
      issuerURL: defaultValues?.spec?.issuerURL
        ? defaultValues.spec.issuerURL.replace(/^https?:\/\//, "")
        : "",
      clientSecret: defaultValues?.spec?.clientSecret || "",
      clientId: defaultValues?.spec?.clientId || "",
      redirectPath: defaultValues?.spec?.redirectPath || "/callback",
      usernameClaim: defaultValues?.spec?.usernameClaim || "",
      emailClaim: defaultValues?.spec?.emailClaim || "",
      roleClaim: defaultValues?.spec?.roleClaim || "",
      groupsClaim: defaultValues?.spec?.groupsClaim || "",
      authorizedDomain: "",
      environmentID: defaultValues?.environmentID || "",
    });
    setDomains(defaultValues?.spec?.authorizedRedirectDomains || []);
    if (defaultValues?.spec?.credentials?.basic?.length) {
      const credentials = defaultValues.spec.credentials.basic.flatMap(
        (i: any, index: number) => ({
          ...i,
          id: Date.now().toString() + index,
        })
      );
      setBasicCredentials(credentials || []);
    }
    if (defaultValues?.spec?.credentials?.token?.length) {
      const tokensValues = defaultValues.spec.credentials.token.flatMap(
        (i: any, index: number) => ({
          ...i,
          id: Date.now().toString() + index,
        })
      );
      setTokens(tokensValues || []);
    }
  }, [defaultValues]);

  useEffect(() => {
    if (!open) {
      setBasicCredentials([defaultBasicCredentials]);
      setTokens([defaultToken]);
      setFormData(defaultFormValues);
      setDomains([]);
      setHasValidationError(false);
    }
  }, [open]);

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const isValidForm = () => {
    const newErrors: IFormErrors = {};
    const validations = [
      {
        condition:
          !isFieldWithValue(formData.name) ||
          !isValidAuthserverName(formData.name),
        field: "name",
        message: "Name is required",
      },
      {
        condition: !isFieldWithValue(formData.environmentID),
        field: "environmentID",
        message: "Environment is required",
      },
      {
        condition: !isFieldWithValue(formData.type),
        field: "type",
        message: "Type is required",
      },
      {
        condition:
          (!isFieldWithValue(formData.issuerURL) ||
            !isValidURL(formData.issuerURL)) &&
          formData.type === EAuthserverTypeOptions.oidc,
        field: "issuerURL",
        message: "Issue URL is required",
      },
      {
        condition:
          !isFieldWithValue(formData.redirectPath) &&
          formData.type === EAuthserverTypeOptions.oidc,
        field: "redirectPath",
        message: "Redirect Path is required",
      },
      {
        condition:
          !isFieldWithValue(formData.clientId) &&
          formData.type === EAuthserverTypeOptions.oidc,
        field: "clientId",
        message: "Client ID is required",
      },
      {
        condition:
          !isFieldWithValue(formData.clientSecret) &&
          formData.type === EAuthserverTypeOptions.oidc,
        field: "clientSecret",
        message: "Client Secret is required",
      },
      {
        condition:
          isFieldWithValue(formData.emailClaim) &&
          !isValidEmail(formData.emailClaim),
        field: "emailClaim",
        message: "Email Claim is required",
      },
    ];

    validations.forEach(({ condition, field, message }) => {
      if (condition) {
        newErrors[field] = message;
      }
    });

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCloseModal = () => {
    onClose();
    setFormErrors({});
    setFormData(defaultFormValues);
    setDomains([]);
    setOpenAccordion(false);
  };

  const onAddAuthorizedDomain = () => {
    if (
      isFieldWithValue(formData.authorizedDomain) &&
      !domains.includes(formData.authorizedDomain)
    ) {
      setDomains((prev) => [...prev, formData.authorizedDomain]);
      handleFormChange("authorizedDomain", "");
    }
  };

  const isValidAuthserverName = (value: string) => {
    const regex = /^[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ-]*$/;
    return regex.test(formData.name);
  };

  const addBasicItemCredential = () => {
    setBasicCredentials([
      ...basicCredentials,
      { id: Date.now().toString(), username: "", password: "" },
    ]);
  };

  const removeBasicItemCredential = (id: any) => {
    setBasicCredentials(basicCredentials.filter((comb) => comb.id !== id));
  };

  const handleChangeBasicItemCredential = (
    id: any,
    field: string,
    value: string
  ) => {
    setBasicCredentials(
      basicCredentials.map((comb) =>
        comb.id === id ? { ...comb, [field]: value } : comb
      )
    );
  };

  const handleChangeTokenValue = (id: any, field: string, value: string) => {
    setTokens(
      tokens.map((val) => (val.id === id ? { ...val, [field]: value } : val))
    );
  };

  const removeTokenValue = (id: any) => {
    setTokens(tokens.filter((val) => val.id !== id));
  };

  const addNewTokenValue = () => {
    setTokens([...tokens, { id: Date.now().toString(), token: "" }]);
  };

  const renderAuthserverTypeContent = useCallback(() => {
    switch (formData.type) {
      case EAuthserverTypeOptions.oidc:
        return (
          <>
            <div className="flex flex-col gap-2">
              <label
                className="text-sm font-medium-500 text-primary"
                htmlFor="issuerURL"
              >
                Issuer URL
              </label>
              <div className="flex flex-row items-center p">
                <div className="text-[1rem] text-[#344054] border border-gray-300 rounded-l-md py-[7px] px-[14px] bg-[#E4E7EC]">
                  https://
                </div>
                <CustomTextFieldLeftContent
                  fullWidth
                  placeholder="Past or type URL"
                  variant="outlined"
                  size="small"
                  name="issuerURL"
                  value={formData.issuerURL}
                  onChange={(e) =>
                    handleFormChange("issuerURL", e.target.value)
                  }
                  error={!!formErrors.issuerURL}
                  helperText=""
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label
                className="text-sm font-medium-500 text-primary"
                htmlFor="redirectPath"
              >
                Redirect Path
              </label>
              <div className="flex flex-row items-center p">
                <div className="text-[1rem] text-[#344054] border border-gray-300 rounded-l-md py-[7px] px-[14px] bg-[#E4E7EC]">
                  /path
                </div>
                <CustomTextFieldLeftContent
                  fullWidth
                  placeholder="Past or type URL"
                  variant="outlined"
                  size="small"
                  name="redirectPath"
                  value={formData.redirectPath}
                  onChange={(e) =>
                    handleFormChange("redirectPath", e.target.value)
                  }
                  error={!!formErrors.redirectPath}
                  helperText=""
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label
                className="text-sm font-medium-500 text-primary"
                htmlFor="clientId"
              >
                Client ID
              </label>
              <TextField
                fullWidth
                placeholder="Enter your ID"
                variant="outlined"
                size="small"
                name="clientId"
                value={formData.clientId}
                onChange={(e) => handleFormChange("clientId", e.target.value)}
                error={!!formErrors.clientId}
                helperText=""
                disabled={isLoading}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label
                className="text-sm font-medium-500 text-primary"
                htmlFor="clientSecret"
              >
                Client Secret
              </label>
              <TextField
                fullWidth
                placeholder="•••••••••"
                variant="outlined"
                size="small"
                name="clientSecret"
                value={formData.clientSecret}
                onChange={(e) =>
                  handleFormChange("clientSecret", e.target.value)
                }
                error={!!formErrors.clientSecret}
                helperText=""
                disabled={isLoading}
                type="password"
              />
            </div>
            <hr className="border-t-[1.8px] border-dashed border-[#E4E7EC] mt-[1rem] mb-[0.5rem] ml-[-30px] mr-[-20px]" />
          <div className="flex flex-col gap-2">
            <label
              className="text-sm font-medium text-[15px] text-primary"
              htmlFor="authorizedDomain"
            >
              Authorized domain
            </label>
            <div className="flex flex-row items-center p">
              <CustomTextFieldRightContent
                fullWidth
                placeholder="Enter your domain"
                variant="outlined"
                size="small"
                name="authorizedDomain"
                value={formData.authorizedDomain}
                onChange={(e) =>
                  handleFormChange("authorizedDomain", e.target.value)
                }
                error={!!formErrors.authorizedDomain}
                helperText=""
                disabled={isLoading}
              />
              <Button
                className={`!text-[#fff] flex xs:h-[40px] sm:h-[40px] md:h-[40px] lg:h-[40px] xl:h-[40px]
              xs:p-[8px] sm:p-[8px] md:p-[15px] lg:p-[15px] xl:p-[15px]
              justify-center items-center gap-[8px] whitespace-nowrap font-normal
              rounded-r-[5px] rounded-l-[0px]
              [background:var(--main-gradient,linear-gradient(90deg,#145BF0_0%,#1DC66D_100%))]
              ${isLoading ? "opacity-50" : ""}`}
                onClick={onAddAuthorizedDomain}
                disabled={isLoading}
              >
                + Add
              </Button>
            </div>
            <div className="flex flex-row gap-2">
              {domains.map((domain) => (
                <div
                  key={domain}
                  className="flex items-center justify-center px-[0.8rem] pt-[0.2rem] pb-[0.3rem] bg-[#F7FAFC] rounded-full border border-[#344054]"
                >
                  <span className="text-[#344054] opacity-45 mr-[0.5rem] text-[11pt]">
                    {domain}
                  </span>
                  <button
                    className="text-[#E02424] mt-[0.2rem]"
                    onClick={() =>
                      setDomains((prev) =>
                        prev.filter((item) => item !== domain)
                      )
                    }
                  >
                    <img src={close_icon} alt="" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <hr className="border-t-[1.8px] border-dashed border-[#E4E7EC] mt-[1rem] mb-[0.5rem] " />
          <div className="flex flex-col gap-2">
            <div className="w-full max-w-md mx-auto space-y-2">
              <div className="rounded-lg">
                <button
                  onClick={() => setOpenAccordion(!openAccordion)}
                  className="w-full flex justify-between items-center p-[0.7rem] bg-[#FAFAFA] text-left rounded-lg border border-[#F2F2F2]"
                >
                  <span>Advanced</span>
                  <span
                    className={`transition-transform ${
                      openAccordion ? "rotate-180" : "rotate-0"
                    }`}
                  >
                    <img src={chevron_top} alt="" className="" />
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all ${
                    openAccordion ? "pt-4 pl-1" : "max-h-0 p-0"
                  }`}
                >
                  <div className="grid grid-cols-2 grid-rows-1 flex-row gap-4 w-full items-baseline mt-2">
                    <div className="flex flex-col gap-2">
                      <label
                        className="text-sm font-medium-500 text-primary"
                        htmlFor="usernameClaim"
                      >
                        Username Claim
                      </label>
                      <TextField
                        fullWidth
                        placeholder="Username"
                        variant="outlined"
                        size="small"
                        name="usernameClaim"
                        value={formData.usernameClaim}
                        onChange={(e) =>
                          handleFormChange("usernameClaim", e.target.value)
                        }
                        error={!!formErrors.usernameClaim}
                        helperText=""
                        disabled={isLoading}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label
                        className="text-sm font-medium-500 text-primary"
                        htmlFor="emailClaim"
                      >
                        Email Claim
                      </label>
                      <TextField
                        fullWidth
                        placeholder="Email"
                        variant="outlined"
                        size="small"
                        name=""
                        value={formData.emailClaim}
                        onChange={(e) =>
                          handleFormChange("emailClaim", e.target.value)
                        }
                        error={!!formErrors.emailClaim}
                        helperText=""
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 grid-rows-1 flex-row gap-4 w-full items-baseline mt-[2rem]">
                    <div className="flex flex-col gap-2">
                      <label
                        className="text-sm font-medium-500 text-primary"
                        htmlFor="roleClaim"
                      >
                        Role Claim
                      </label>
                      <TextField
                        fullWidth
                        placeholder="Role"
                        variant="outlined"
                        size="small"
                        name="roleClaim"
                        value={formData.roleClaim}
                        onChange={(e) =>
                          handleFormChange("roleClaim", e.target.value)
                        }
                        error={!!formErrors.roleClaim}
                        helperText=""
                        disabled={isLoading}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label
                        className="text-sm font-medium-500 text-primary"
                        htmlFor="groupsClaim"
                      >
                        Group Claim
                      </label>
                      <TextField
                        fullWidth
                        placeholder="Group"
                        variant="outlined"
                        size="small"
                        name=""
                        value={formData.groupsClaim}
                        onChange={(e) =>
                          handleFormChange("groupsClaim", e.target.value)
                        }
                        error={!!formErrors.groupsClaim}
                        helperText=""
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </>
        );
      case EAuthserverTypeOptions.basic:
        return (
          <>
            {basicCredentials.map((cred, index) => (
              <div
                key={cred.id || `key-${index}`}
                className="flex items-center space-x-2"
              >
                <div className="flex flex-col gap-2">
                  <TextField
                    fullWidth
                    placeholder="username"
                    variant="outlined"
                    size="small"
                    name="username"
                    value={cred.username}
                    onChange={(e) =>
                      handleChangeBasicItemCredential(
                        cred.id,
                        "username",
                        e.target.value
                      )
                    }
                    error={
                      (isFieldWithValue(cred.username) &&
                        !usernameRegex.test(cred.username)) ||
                      (hasValidationError && !isFieldWithValue(cred.username))
                    }
                    disabled={isLoading}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <TextField
                    fullWidth
                    placeholder="password"
                    variant="outlined"
                    size="small"
                    name="password"
                    value={cred.password}
                    onChange={(e) =>
                      handleChangeBasicItemCredential(
                        cred.id,
                        "password",
                        e.target.value
                      )
                    }
                    error={
                      hasValidationError &&
                      (!isFieldWithValue(cred.password) ||
                        cred.password.length < 4)
                    }
                    disabled={isLoading}
                    type="password"
                  />
                </div>
                {basicCredentials.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeBasicItemCredential(cred.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    {ICONS.TrashIcon()}
                  </button>
                )}
              </div>
            ))}
            <div className="flex flex-row-reverse">
              <Button
                className={`!text-[#fff] flex xs:h-[40px] sm:h-[40px] md:h-[40px] lg:h-[40px] xl:h-[40px]
              xs:p-[8px] sm:p-[8px] md:p-[15px] lg:p-[15px] xl:p-[15px]
              justify-center items-center gap-[8px] whitespace-nowrap font-normal

              [background:var(--main-gradient,linear-gradient(90deg,#145BF0_0%,#1DC66D_100%))]
              ${isLoading ? "opacity-50" : ""}`}
                onClick={addBasicItemCredential}
                disabled={isLoading}
              >
                + Add
              </Button>
            </div>
          </>
        );
      case EAuthserverTypeOptions.token:
        return (
          <>
            {tokens.map((value, index) => (
              <div
                className="flex flex-row gap-2"
                key={value.id || `key-${index}`}
              >
                <TextField
                  fullWidth
                  placeholder="token"
                  variant="outlined"
                  size="small"
                  name="token"
                  value={value.token}
                  onChange={(e) =>
                    handleChangeTokenValue(value.id, "token", e.target.value)
                  }
                  error={
                    (isFieldWithValue(value.token) &&
                      !tokenRegex.test(value.token)) ||
                    (hasValidationError && !isFieldWithValue(value.token))
                  }
                  disabled={isLoading}
                />
                {tokens.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTokenValue(value.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    {ICONS.TrashIcon()}
                  </button>
                )}
              </div>
            ))}
            <div className="flex flex-row-reverse">
              <Button
                className={`!text-[#fff] flex xs:h-[40px] sm:h-[40px] md:h-[40px] lg:h-[40px] xl:h-[40px]
                xs:p-[8px] sm:p-[8px] md:p-[15px] lg:p-[15px] xl:p-[15px]
                justify-center items-center gap-[8px] whitespace-nowrap font-normal
  
                [background:var(--main-gradient,linear-gradient(90deg,#145BF0_0%,#1DC66D_100%))]
                ${isLoading ? "opacity-50" : ""}`}
                onClick={addNewTokenValue}
                disabled={isLoading}
              >
                + Add
              </Button>
            </div>
          </>
        );
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, basicCredentials, hasValidationError, tokens, openAccordion, domains]);

  const isValidBasicCredentials = () => {
    return formData.type === EAuthserverTypeOptions.basic
      ? basicCredentials.every(
          (comb) =>
            usernameRegex.test(comb.username) && comb.password.length > 3
        )
      : true;
  };

  const isValidAllTokens = () => {
    return formData.type === EAuthserverTypeOptions.token
      ? tokens.every(
          (val) =>
            tokenRegex.test(val.token)
        )
      : true;
  };

  const handleSubmit = () => {
    try {
      if (isValidForm() && isValidBasicCredentials() && isValidAllTokens()) {
        const issuerURL =
          !formData.issuerURL.startsWith("http://") &&
          !formData.issuerURL.startsWith("https://")
            ? `https://${formData.issuerURL}`
            : formData.issuerURL;
        const authorizedDomains = domains;
        if (isFieldWithValue(formData.authorizedDomain) && !authorizedDomains.includes(formData.authorizedDomain)) {
          authorizedDomains.push(formData.authorizedDomain);
        }
        onSubmit({
          ...formData,
          issuerURL,
          domains: [...new Set(authorizedDomains)],
          basicCredentials,
          tokens
        });
      } else {
        setHasValidationError(true);
      }
    } catch (error) {
      console.log({ error });
    }
  };

  return (
    <SimpleModal
      open={open}
      onClose={handleCloseModal}
      title={title}
      onSubmit={handleSubmit}
      description={description}
      submitTitleButton={submitTitleButton}
      maxWidth="xs"
      isLoading={isLoading}
    >
      <div className="flex flex-col items-start justify-start">
        <div className="grid grid-cols-1 grid-rows-1 flex-row gap-4 w-full items-baseline">
          <div className="flex flex-col gap-2">
            <label
              className="text-sm font-medium-500 text-primary"
              htmlFor="name"
            >
              Name
            </label>
            <TextField
              fullWidth
              placeholder="Enter auth name"
              variant="outlined"
              size="small"
              name="name"
              value={formData.name}
              onChange={(e) => handleFormChange("name", e.target.value)}
              error={!!formErrors.name}
              helperText="" //formErrors.name
              disabled={isLoading || isEdition}
            />
          </div>
          <div className="grid grid-cols-1 grid-rows-1 flex-row gap-4 w-full items-baseline mt-2">
            <div className="flex flex-col gap-2">
              <label
                className="text-sm font-medium-500 text-primary"
                htmlFor="environmentID"
              >
                Environment
              </label>
              <SelectMenu
                options={environments}
                onChange={(value) => handleFormChange("environmentID", value)}
                placeholder="Select environment"
                defaultValue={formData.environmentID}
                disabled={isLoading || isEdition}
                error={!!formErrors.environmentID}
                helpertext=""
                height="9rem"
                className={
                  !!formErrors.environmentID
                    ? "border border-red-500 rounded-md"
                    : ""
                }
                positionOptions="fixed"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 grid-rows-1 flex-row gap-4 w-full items-baseline mt-2">
            <div className="flex flex-col gap-2">
              <label
                className="text-sm font-medium-500 text-primary"
                htmlFor="type"
              >
                Type
              </label>
              <SelectMenu
                options={AuthserverTypeOptions}
                onChange={(value) => handleFormChange("type", value)}
                placeholder="Select one"
                defaultValue={formData.type}
                disabled={isLoading || isEdition}
                error={!!formErrors.type}
                helpertext=""
                height="9rem"
                className={
                  !!formErrors.type ? "border border-red-500 rounded-md" : ""
                }
                positionOptions="fixed"
              />
            </div>
          </div>
          {renderAuthserverTypeContent()}
          
        </div>
      </div>
    </SimpleModal>
  );
}
